import { useState, useMemo } from 'react';
import type { Block, VocabularyGridItem, WorkbookProfile } from '@ellie/shared';
import { buildPersonalizationClause } from '@ellie/shared';
import { useStudioStore } from '@/stores/useStudioStore';

type TabId = 'form' | 'json';

interface BlockEditorProps {
  block: Block;
  onChange: (updated: Block) => void;
}

export function BlockEditor({ block, onChange }: BlockEditorProps) {
  const { profile } = useStudioStore();
  const [activeTab, setActiveTab] = useState<TabId>('form');
  const [jsonValue, setJsonValue] = useState(() => JSON.stringify(block, null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Build personalization clause for this block using the studio profile
  const personalizationClause = useMemo(() => {
    const fakeProfile = {
      ...profile,
      id: 'studio',
      user_id: 'studio',
      shipping_name: null,
      shipping_address_line1: null,
      shipping_address_line2: null,
      shipping_city: null,
      shipping_postal_code: null,
      shipping_country: null,
      is_active: false,
      created_at: '',
      updated_at: '',
    } as WorkbookProfile;
    return buildPersonalizationClause(fakeProfile, block.order);
  }, [profile, block.order]);

  function handleFieldChange(field: string, value: string) {
    onChange({ ...block, [field]: value } as Block);
  }

  function handleJsonBlur() {
    try {
      const parsed = JSON.parse(jsonValue) as Block;
      setJsonError(null);
      onChange(parsed);
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : 'Invalid JSON');
    }
  }

  // Sync JSON textarea when switching to JSON tab or when block changes externally
  function handleTabSwitch(tab: TabId) {
    if (tab === 'json') {
      setJsonValue(JSON.stringify(block, null, 2));
      setJsonError(null);
    }
    setActiveTab(tab);
  }

  return (
    <div className="rounded-lg border border-border bg-white">
      {/* Header */}
      <div className="border-b border-border-light px-4 py-3">
        <h3 className="font-heading text-lg text-brand-dark">{block.title}</h3>
        <p className="text-xs text-text-muted">
          {block.type.replace(/_/g, ' ')} &middot; Block #{block.order}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border-light">
        <button
          onClick={() => handleTabSwitch('form')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'form'
              ? 'border-b-2 border-brand text-brand'
              : 'text-text-muted hover:text-text-primary'
          }`}
        >
          Form
        </button>
        <button
          onClick={() => handleTabSwitch('json')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'json'
              ? 'border-b-2 border-brand text-brand'
              : 'text-text-muted hover:text-text-primary'
          }`}
        >
          JSON
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {activeTab === 'form' ? (
          <FormTab
            block={block}
            onFieldChange={handleFieldChange}
            onChange={onChange}
            personalizationClause={personalizationClause}
          />
        ) : (
          <JsonTab
            value={jsonValue}
            error={jsonError}
            onValueChange={setJsonValue}
            onBlur={handleJsonBlur}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form Tab
// ---------------------------------------------------------------------------

interface FormTabProps {
  block: Block;
  onFieldChange: (field: string, value: string) => void;
  onChange: (updated: Block) => void;
  personalizationClause: string;
}

function FormTab({ block, onFieldChange, onChange, personalizationClause }: FormTabProps) {
  return (
    <div className="space-y-4">
      {/* Title */}
      <FieldGroup label="Title">
        <input
          type="text"
          value={block.title}
          onChange={(e) => onFieldChange('title', e.target.value)}
          className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
      </FieldGroup>

      {/* Instructions (for types that have them) */}
      {'instructions' in block && typeof block.instructions === 'string' && (
        <FieldGroup label="Instructions">
          <textarea
            value={block.instructions}
            onChange={(e) => onFieldChange('instructions', e.target.value)}
            rows={3}
            className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          />
        </FieldGroup>
      )}

      {/* Personalization clause */}
      {personalizationClause && (
        <FieldGroup label="Personalization Clause">
          <pre className="max-h-48 overflow-auto rounded-md bg-purple-50 p-3 text-xs leading-relaxed text-purple-900 whitespace-pre-wrap">
            {personalizationClause}
          </pre>
        </FieldGroup>
      )}

      {/* Vocabulary Grid items editor */}
      {block.type === 'vocabulary_grid' && (
        <VocabularyGridEditor block={block} onChange={onChange} />
      )}

      {/* Read-only preview for other complex fields */}
      {block.type !== 'vocabulary_grid' && (
        <FieldGroup label="Block Data">
          <pre className="max-h-64 overflow-auto rounded-md bg-gray-50 p-3 text-xs text-text-muted">
            {JSON.stringify(block, null, 2)}
          </pre>
        </FieldGroup>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vocabulary Grid Item Editor
// ---------------------------------------------------------------------------

interface VocabularyGridEditorProps {
  block: Block & { type: 'vocabulary_grid' };
  onChange: (updated: Block) => void;
}

function VocabularyGridEditor({ block, onChange }: VocabularyGridEditorProps) {
  function handleItemChange(
    index: number,
    field: keyof Pick<VocabularyGridItem, 'target' | 'native'>,
    value: string,
  ) {
    const updatedItems = block.items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item,
    );
    onChange({ ...block, items: updatedItems });
  }

  return (
    <FieldGroup label="Vocabulary Items">
      <div className="space-y-2">
        {block.items.map((item, index) => (
          <div key={index} className="flex gap-2">
            <input
              type="text"
              value={item.target}
              onChange={(e) => handleItemChange(index, 'target', e.target.value)}
              placeholder="Target language"
              className="flex-1 rounded-md border border-border px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
            <input
              type="text"
              value={item.native}
              onChange={(e) => handleItemChange(index, 'native', e.target.value)}
              placeholder="Native language"
              className="flex-1 rounded-md border border-border px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>
        ))}
      </div>
    </FieldGroup>
  );
}

// ---------------------------------------------------------------------------
// JSON Tab
// ---------------------------------------------------------------------------

interface JsonTabProps {
  value: string;
  error: string | null;
  onValueChange: (value: string) => void;
  onBlur: () => void;
}

function JsonTab({ value, error, onValueChange, onBlur }: JsonTabProps) {
  return (
    <div className="space-y-2">
      <textarea
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onBlur={onBlur}
        rows={20}
        spellCheck={false}
        className={`w-full rounded-md border px-3 py-2 font-mono text-xs focus:outline-none focus:ring-1 ${
          error
            ? 'border-red-400 focus:border-red-400 focus:ring-red-400'
            : 'border-border focus:border-brand focus:ring-brand'
        }`}
      />
      {error && <p className="text-xs text-red-500">Invalid JSON: {error}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared Field Group
// ---------------------------------------------------------------------------

interface FieldGroupProps {
  label: string;
  children: React.ReactNode;
}

function FieldGroup({ label, children }: FieldGroupProps) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-text-muted uppercase tracking-wide">
        {label}
      </label>
      {children}
    </div>
  );
}
